import React, { useRef, useState } from 'react';
import { Box, Button, Container, Theme, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { KeyboardArrowUp } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { getCountries } from '../services/country';
import { getUniversities, deserialize, QUERY } from '../services/university';
import Header from '../components/Header';
import Map from '../components/Map';
import SearchBox from '../components/SearchBox';
import FilterBox from '../components/FilterBox';
import ResultBox from '../components/ResultBox';

const filterHeight: number = 96;
const filterHeightSM: number = 152;

const popupVariants = {
  hidden: {
    height: 0,
  },
  visible: {
    height: '70vh',
  },
};

const useStyles = makeStyles((theme: Theme) => ({
  app: {
    overflowY: 'hidden',
    position: 'relative',
  },
  map: {
    height: `calc(100vh)`,
    position: 'relative',
  },
  popupButton: {
    bottom: '5vh',
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
  },
  resultBox: {
    backgroundColor: '#F8F8F9',
    height: `calc(70vh - ${filterHeight}px)`,
    overflowY: 'scroll',
    '@media (max-width:480px)': {
      height: `calc(70vh - ${filterHeightSM}px)`,
    },
  },
  resultPanel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  searchBox: {
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: '12vh',
  },
}));

const Home: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [faculties, setFaculties] = useState<string[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const resultBoxRef = useRef<HTMLDivElement>(null);
  const {
    app,
    map,
    popupButton,
    resultBox,
    resultPanel,
    searchBox,
  } = useStyles();
  const theme = useTheme();

  const { data } = useQuery(QUERY, { variables: { first: 10000 } });

  const handleCountryChange = (values: Country[]) => {
    setCountries(values);
  };

  const handleFacultyChange = (values: string[]) => {
    setFaculties(values);
  };

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
    setShowPopup(true);
  };

  const handleReset = () => {
    setKeyword('');
    setCountries([]);
    setFaculties([]);
    setShowPopup(false);
  };

  const handleMapClick = () => {
    if (showPopup) setShowPopup(false);
  };

  const handleMarkerClick = (country: string) => {
    setCountries([{ title: country, latitude: 0, longitude: 0, count: 0 }]);
    setShowPopup(true);
  };

  const nodes: any[] =
    data?.universities.edges.map((edge: any) => edge.node) ?? [];
  const allUniversities = nodes.map(deserialize);
  const results = getUniversities(
    allUniversities,
    countries,
    faculties,
    keyword
  );

  return (
    <Box className={app}>
      <Header
        countryCount={getCountries(results).length}
        universityCount={results.length}
      />
      <div className={map}>
        <Map
          countries={getCountries(results)}
          onMapClick={handleMapClick}
          onMarkerClick={handleMarkerClick}
        />
      </div>
      <SearchBox
        className={searchBox}
        filtering={!!keyword || !!countries.length || !!faculties.length}
        onSearch={handleKeywordChange}
        onReset={handleReset}
      />
      <div className={popupButton}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="outlined"
            onClick={() => setShowPopup(true)}
            color="primary"
          >
            Show Results
            <KeyboardArrowUp />
          </Button>
        </Box>
      </div>
      <motion.div
        className={resultPanel}
        variants={popupVariants}
        initial={'hidden'}
        transition={{ ease: 'easeOut', duration: 0.2 }}
        animate={showPopup ? 'visible' : 'hidden'}
      >
        <Box display="flex" justifyContent="center"></Box>
        <Box bgcolor={theme.palette.primary.main}>
          <Container maxWidth="md">
            <FilterBox
              countries={countries}
              faculties={faculties}
              countryOptions={getCountries(allUniversities)}
              onClose={() => setShowPopup(false)}
              onCountryChange={handleCountryChange}
              onFacultyChange={handleFacultyChange}
            />
          </Container>
        </Box>
        <div className={resultBox} ref={resultBoxRef}>
          <Container maxWidth="md">
            <ResultBox
              results={results}
              onPageChange={() => {
                if (resultBoxRef.current) resultBoxRef.current.scrollTo(0, 0);
              }}
            />
          </Container>
        </div>
      </motion.div>
    </Box>
  );
};

export default Home;
